import { useState, useEffect } from "react";
import { PerformanceThreshold, getDefaultThresholds } from "@/types/performanceThresholds";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RotateCcw, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface PerformanceThresholdSettingsProps {
  thresholds: PerformanceThreshold[];
  onSave: (thresholds: PerformanceThreshold[]) => void;
}

const PerformanceThresholdSettings = ({ thresholds, onSave }: PerformanceThresholdSettingsProps) => {
  const [localThresholds, setLocalThresholds] = useState<PerformanceThreshold[]>(thresholds);

  useEffect(() => {
    setLocalThresholds(thresholds);
  }, [thresholds]);

  const handleThresholdChange = (index: number, value: number) => {
    const updated = [...localThresholds];
    updated[index] = { ...updated[index], minPercentage: value };
    setLocalThresholds(updated);
  };

  const handleLevelChange = (index: number, value: string) => {
    const updated = [...localThresholds];
    updated[index] = { ...updated[index], level: value };
    setLocalThresholds(updated);
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updated = [...localThresholds];
    updated[index] = { ...updated[index], description: value };
    setLocalThresholds(updated);
  };

  const handleAddThreshold = () => {
    const newThreshold: PerformanceThreshold = {
      level: `Neue Stufe ${localThresholds.length + 1}`,
      minPercentage: 0,
      color: 'primary',
      description: ''
    };
    setLocalThresholds([...localThresholds, newThreshold]);
  };

  const handleDeleteThreshold = (index: number) => {
    if (localThresholds.length <= 1) {
      toast.error("Mindestens ein Schwellenwert muss vorhanden sein");
      return;
    }
    const updated = localThresholds.filter((_, i) => i !== index);
    setLocalThresholds(updated);
  };

  const handleSave = () => {
    // Validate that thresholds are in descending order
    const sorted = [...localThresholds].sort((a, b) => b.minPercentage - a.minPercentage);
    const isValid = localThresholds.every((t, i) => {
      const sortedIndex = sorted.findIndex(s => s.level === t.level && s.minPercentage === t.minPercentage);
      return sortedIndex !== -1;
    });
    
    // Auto-sort before saving
    const sortedThresholds = [...localThresholds].sort((a, b) => b.minPercentage - a.minPercentage);
    onSave(sortedThresholds);
    setLocalThresholds(sortedThresholds);
    toast.success("Schwellenwerte gespeichert");
  };

  const handleReset = () => {
    const defaults = getDefaultThresholds();
    setLocalThresholds(defaults);
    onSave(defaults);
    toast.success("Standardwerte wiederhergestellt");
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle>Leistungsschwellenwerte</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Definieren Sie die Prozentsätze für die verschiedenen Leistungsniveaus
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleAddThreshold} className="gap-2">
            <Plus className="h-4 w-4" />
            Hinzufügen
          </Button>
          <Button variant="outline" onClick={handleReset} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Zurücksetzen
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {/* Don't sort during editing - only sort on save */}
          {localThresholds.map((threshold, index) => (
            <Card key={`threshold-${index}`} className="border-2">
              <CardContent className="pt-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`level-${index}`}>Bewertungstext</Label>
                      <Input
                        id={`level-${index}`}
                        value={threshold.level}
                        onChange={(e) => handleLevelChange(index, e.target.value)}
                        placeholder="z.B. Sehr gut"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`threshold-${index}`}>Mindestprozentsatz (%)</Label>
                      <div className="flex gap-2 items-center">
                        <Input
                          id={`threshold-${index}`}
                          type="number"
                          min="0"
                          max="200"
                          value={threshold.minPercentage}
                          onChange={(e) => handleThresholdChange(index, parseInt(e.target.value) || 0)}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleDeleteThreshold(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`description-${index}`}>Hinweis / Beschreibung</Label>
                  <Textarea
                    id={`description-${index}`}
                    value={threshold.description || ''}
                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                    placeholder="Optionale Beschreibung für diese Bewertungsstufe..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <span>ℹ️</span> Hinweis
          </h4>
          <p className="text-sm text-muted-foreground">
            Die Schwellenwerte werden beim Speichern automatisch nach Prozentsatz sortiert. 
            Der höchste Wert stellt die beste Bewertung dar. Werte bis 200% sind möglich,
            um Bonuspunkte abzubilden.
          </p>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={handleSave} size="lg">
            Schwellenwerte speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PerformanceThresholdSettings;
